<template>
<v-form
    ref="ruleForm"
    v-model="formValid"
    lazy-validation
  >
  <div>
    <v-card outlined>
      <v-card-title class="grey lighten-3">
        <slot name="title">{{ title }}</slot>
        <v-spacer></v-spacer>
        <v-switch
          v-if="allowIgnoreServiceLimitTIme"
          class="mt-0"
          v-model="showServiceLimitTime"
          label="Show active items"
          hide-details
        ></v-switch>
        <slot name="headerButtons"> </slot>
      </v-card-title>
      <v-card-text>
        <v-row dense class="mx-2 mt-4">
          <v-col cols="12" sm="4" md="4">
            <v-select
              dense
              label="Organisation"
              v-model="search.organisation"
              :items="headerSelects['organisation']"
              item-value="value"
              @change="searchUpdated"
              :rules="[$common.rules.required]"
              class="required"
              outlined
              clearable
              :disabled="organisationDisabled"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-select
              dense
              label="Country"
              v-model="search.country"
              :items="headerSelects['country']"
              item-value="value"
              item-text="text"
              @change="searchUpdated"
              :rules="[$common.rules.requiredArray]"
              class="required"
              outlined
              clearable
              multiple
            >
              <template v-slot:prepend-item>
                <v-list-item @click="toggleSelectAllCountries">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isAllCountriesSelected"
                      :indeterminate="isCountriesIndeterminate"
                      color="primary"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
              </template>

              <template #selection="{ item, index }">
                <span v-if="index === 0">
                  <template v-if="isAllCountriesSelected"> ALL </template>
                  <template v-else>
                    {{
                      search.country
                        .map((v) => {
                          const found = headerSelects.country.find(
                            (i) => i.value === v
                          );
                          return found ? found.text : v;
                        })
                        .join(", ")
                    }}
                  </template>
                </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-select
              dense
              label="SBM Profile"
              v-model="search.sbmProfile"
              :items="headerSelects['sbmProfile']"
              item-value="value"
              @change="searchUpdated"
              :rules="[$common.rules.requiredArray]"
              class="required"
              outlined
              clearable
              multiple
            >
              <template v-slot:prepend-item>
                <v-list-item @click="toggleSelectAllSBMProfiles">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isAllSBMProfilesSelected"
                      :indeterminate="isSBMProfilesIndeterminate"
                      color="primary"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
              </template>

              <template #selection="{ item, index }">
                <span v-if="index === 0">
                  <template v-if="isAllSBMProfilesSelected"> ALL </template>
                  <template v-else>
                    {{
                      search.sbmProfile
                        .map((v) => {
                          const found = headerSelects.sbmProfile.find(
                            (i) => i.value === v
                          );
                          return found ? found.text : v;
                        })
                        .join(", ")
                    }}
                  </template>
                </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="4" md="4" v-if="!isSpecificDocumentMode">
            <v-select
              dense
              label="Document Type"
              v-model="search.documentType"
              :items="headerSelects['documentType']"
              item-value="value"
              item-text="text"
              outlined
              multiple
              clearable
              @change="searchUpdated"
              :menu-props="{ closeOnContentClick: false }"
              :rules="[$common.rules.requiredArray]"
              class="required"
            >
              <template v-slot:prepend-item>
                <v-list-item @click="toggleSelectAllDocumentTypes">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isAllDocumentTypesSelected"
                      :indeterminate="isDocumentTypesIndeterminate"
                      color="primary"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
              </template>

              <template #selection="{ item, index }">
                <span v-if="index === 0">
                  <template v-if="isAllDocumentTypesSelected"> ALL </template>
                  <template v-else>
                    {{
                      search.documentType
                        .map((v) => {
                          const found = headerSelects.documentType.find(
                            (i) => i.value === v
                          );
                          return found ? found.text : v;
                        })
                        .join(", ")
                    }}
                  </template>
                </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="4" md="4" v-if="showServiceGrade && !isSpecificDocumentMode">
            <v-select
              dense
              label="Service Grade"
              v-model="search.serviceGrade"
              :items="headerSelects['serviceGrade']"
              item-value="value"
              item-text="text"
              outlined
              multiple
              clearable
              @change="searchUpdated"
              :menu-props="{ closeOnContentClick: false }"
              :rules="serviceGradeRules"
              class="required"
            >
              <template v-slot:prepend-item>
                <v-list-item @click="toggleSelectAllServiceGrades">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isAllServiceGradesSelected"
                      :indeterminate="isServiceGradesIndeterminate"
                      color="primary"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
              </template>

              <template #selection="{ item, index }">
                <span v-if="index === 0">
                  <template v-if="isAllServiceGradesSelected"> ALL </template>
                  <template v-else>
                    {{
                      search.serviceGrade
                        .map((v) => {
                          const found = headerSelects.serviceGrade.find(
                            (i) => i.value === v
                          );
                          return found ? found.text : v;
                        })
                        .join(", ")
                    }}
                  </template>
                </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="4" md="4" v-if="!isSpecificDocumentMode">
            <v-select
              dense
              label="Product Group"
              v-model="search.productGroup"
              :items="headerSelects['productGroup'] || []"
              item-value="value"
              item-text="text"
              outlined
              multiple
              clearable
              @change="searchUpdated"
              :rules="[$common.rules.requiredArray]"
              class="required"
            >
              <template v-slot:prepend-item>
                <v-list-item @click="toggleSelectAllProductGroups">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isAllProductGroupsSelected"
                      :indeterminate="isProductGroupsIndeterminate"
                      color="primary"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
              </template>

              <template #selection="{ item, index }">
                <span v-if="index === 0">
                  <template v-if="isAllProductGroupsSelected"> ALL </template>
                  <template v-else>
                    {{
                      search.productGroup
                        .map((v) => {
                          const found = headerSelects.productGroup.find(
                            (i) => i.value === v
                          );
                          return found ? found.text : v;
                        })
                        .join(", ")
                    }}
                  </template>
                </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-select
              dense
              label="Info Level"
              v-model="search.infoLevel"
              :items="headerSelects['infoLevel']"
              item-text="text"
              item-value="text"
              @change="searchUpdated"
              :rules="[$common.rules.required]"
              class="required"
              outlined
              clearable
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            :class="$vuetify.breakpoint.smAndUp ? 'pl-1 pb-2' : 'pb-2'"
          >
            <div class="required">
              <date-picker
                required
                label="Active Date From"
                v-model="search.activeDateFrom"
                @change="searchUpdated"
              ></date-picker>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            :class="$vuetify.breakpoint.smAndUp ? 'pl-1 pb-2' : 'pb-2'"
          >
            <date-picker
              label="Active Date To"
              v-model="search.activeDateTo"
            ></date-picker>
          </v-col>
        </v-row>
        <v-data-table
          v-if="documentMode === 'specific'"
          :headers="filteredHeadersTable"
          dense
          :items="esvs"
          show-select
          item-key="id"
          v-model="internalSelected"
          :footer-props="{
            'items-per-page-options': [15, 50, 100, -1],
          }"
          :items-per-page="50"
          @click:row="routeToEsv"
          :options.sync="options"
          :server-items-length="totalEsvs"
          multi-sort
          :loading="loading"
          :item-class="itemRowBackground"
        >
          <template v-slot:header="{ props: { headers } }">
            <tr v-show="$vuetify.breakpoint.smAndUp">
              <th v-for="header in headers" :key="header.value">
                <div
                  v-if="header.type === 'text'"
                  class="mx-2 font-weight-light"
                >
                  <v-text-field
                    dense
                    :label="header.text"
                    clearable
                    v-model="search[header.value]"
                    v-on:input="searchUpdated"
                    outlined
                  ></v-text-field>
                </div>
                <div
                  v-else-if="header.type === 'select'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    v-on:change="searchUpdated"
                    outlined
                    clearable
                  ></v-select>
                </div>
                <div
                  v-else-if="header.type === 'multiselect'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    v-on:change="searchUpdated"
                    outlined
                    clearable
                    multiple
                  ></v-select>
                </div>
              </th>
            </tr>
            <tr
              v-show="$vuetify.breakpoint.xsOnly"
              v-for="header in headers"
              :key="header.value"
            >
              <th>
                <div
                  v-if="header.type === 'text'"
                  class="mx-2 font-weight-light"
                >
                  <v-text-field
                    dense
                    :label="header.text"
                    clearable
                    v-model="search[header.value]"
                    v-on:input="searchUpdated"
                    outlined
                  ></v-text-field>
                </div>
                <div
                  v-else-if="header.type === 'select'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    v-on:change="searchUpdated"
                    outlined
                    clearable
                  ></v-select>
                </div>
              </th>
            </tr>
          </template>
        </v-data-table>
        <v-card
          flat
          class="pa-3 mt-3 rounded-lg"
          color="blue-grey lighten-5"
          v-if="documentMode === 'all'"
        >
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-file-multiple</v-icon>
            <span class="text-subtitle-2">
              All documents are automatically included.
            </span>
          </div>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
  </v-form>
</template>

<script>
// UI uses `documentType`, backend uses `serviceRequestType`

import _ from "lodash";
import DatePicker from "@/components/common/DatePicker.vue";

export default {
  name: "DataTable",
  props: {
    organisationDisabled: {
      type: Boolean,
      default: false,
    },
    documentMode: String,
    selectedItems: {
      type: Array,
      default: () => [],
    },
    esvs: {
      type: Array,
      required: true,
    },
    title: {
      typeof: String,
      default: "Search Results",
    },
    optionsPassed: {
      typeof: Array,
      required: true,
    },
    totalEsvs: {
      typeof: Number,
      required: true,
    },
    esvs: {
      typeof: Array,
      required: true,
    },
    searchPassed: {
      typeof: Object,
      required: true,
    },
    headerSelects: {
      typeof: Array,
      required: true,
    },
    headersTable: {
      typeof: Array,
      required: true,
    },
    loading: Boolean,
    allowIgnoreServiceLimitTIme: {
      typeof: Boolean,
      default: true,
    },
    showHasApproved: {
      typeof: Boolean,
      default: false,
    },
    routeToDetails: {
      typeof: Boolean,
      default: true,
    },
  },
  components: {
    ApprovalSchematic: () => import("@/components/ESV/ApprovalSchematic.vue"),
    DatePicker,
  },
  data() {
    return {
      // loading: false,
      formValid: false,
      searchTimeOut: null,
      options: this.optionsPassed,
      search: this.searchPassed,
      showServiceLimitTime: true,
      pendingSearchUpdate: false,
    };
  },
computed: {
  isSpecificDocumentMode() {
    return this.documentMode === "specific";
  },
  isOnlyRNS() {
    return (
      Array.isArray(this.search.documentType) &&
      this.search.documentType.length === 1 &&
      this.search.documentType.includes(24)
    );
  },

  showServiceGrade() {
    // standaard zichtbaar, behalve bij alleen RNS
    return !this.isOnlyRNS;
  },

  serviceGradeRules() {
    // alleen verplicht als ESV actief is
    if (this.includesESV) {
      return [
        v => (Array.isArray(v) && v.length > 0) || 'Required field'
      ];
    }
    return [];
  },
    includesESV() {
      return Array.isArray(this.search.documentType) &&
            this.search.documentType.includes(1);
    },
    includesRNS() {
      return Array.isArray(this.search.documentType) &&
            this.search.documentType.includes(24);
    },
    internalSelected: {
      get() {
        return Array.isArray(this.selectedItems) ? this.selectedItems : [];
      },
      set(newVal) {
        this.$emit("update:selectedItems", newVal);
      },
    },
    isAllDocumentTypesSelected() {
      return (
        this.search.documentType &&
        this.search.documentType.length ===
          this.headerSelects.documentType.length
      );
    },
    isDocumentTypesIndeterminate() {
      return (
        this.search.documentType &&
        this.search.documentType.length > 0 &&
        //this.search.documentType.length < this.headerSelects.documentType.length
        this.search.documentType.length < 2
      );
    },
    isAllProductGroupsSelected() {
      return (
        this.search.productGroup &&
        this.search.productGroup.length ===
          this.headerSelects.productGroup.length
      );
    },
    isProductGroupsIndeterminate() {
      return (
        this.search.productGroup &&
        this.search.productGroup.length > 0 &&
        this.search.productGroup.length < this.headerSelects.productGroup.length
      );
    },

    isAllServiceGradesSelected() {
      return (
        this.search.serviceGrade &&
        this.search.serviceGrade.length ===
          this.headerSelects.serviceGrade.length
      );
    },

    isAllCountriesSelected() {
      return (
        this.search.country &&
        this.search.country.length === this.headerSelects.country.length
      );
    },

    isAllSBMProfilesSelected() {
      return (
        this.search.sbmProfile &&
        this.search.sbmProfile.length === this.headerSelects.sbmProfile.length
      );
    },

    isServiceGradesIndeterminate() {
      return (
        this.search.serviceGrade &&
        this.search.serviceGrade.length > 0 &&
        this.search.serviceGrade.length < this.headerSelects.serviceGrade.length
      );
    },

    isCountriesIndeterminate() {
      return (
        this.search.country &&
        this.search.country.length > 0 &&
        this.search.country.length < this.headerSelects.country.length
      );
    },

    isSBMProfilesIndeterminate() {
      return (
        this.search.sbmProfile &&
        this.search.sbmProfile.length > 0 &&
        this.search.sbmProfile.length < this.headerSelects.sbmProfile.length
      );
    },

    filteredHeadersTable() {
      if (this.search.serviceRequestType === 24) {
        return this.headersTable.filter(
          (header) => header.value !== "serviceGrade"
        );
      }
      return this.headersTable;
    },
  },
  methods: {
    validateForm() {
      return this.$refs.ruleForm.validate();
    },

    toggleSelectAllDocumentTypes() {
      if (this.isAllDocumentTypesSelected) {
        this.search.documentType = [];
      } else {
        this.search.documentType = this.headerSelects.documentType.map(
          (pg) => pg.value
        );
      }
      this.searchUpdated();
    },

    toggleSelectAllProductGroups() {
      if (this.isAllProductGroupsSelected) {
        this.search.productGroup = [];
      } else {
        this.search.productGroup = this.headerSelects.productGroup.map(
          (pg) => pg.value
        );
      }
      this.searchUpdated();
    },

    toggleSelectAllServiceGrades() {
      if (this.isAllServiceGradesSelected) {
        this.search.serviceGrade = [];
      } else {
        this.search.serviceGrade = this.headerSelects.serviceGrade.map(
          (sg) => sg.value
        );
      }
      this.searchUpdated();
    },

    toggleSelectAllCountries() {
      if (this.isAllCountriesSelected) {
        this.search.country = [];
      } else {
        this.search.country = this.headerSelects.country.map((c) => c.value);
      }
      this.searchUpdated();
    },

    toggleSelectAllSBMProfiles() {
      if (this.isAllSBMProfilesSelected) {
        this.search.sbmProfile = [];
      } else {
        this.search.sbmProfile = this.headerSelects.sbmProfile.map(
          (sp) => sp.value
        );
      }
      this.searchUpdated();
    },

    routeToEsv(esv) {
      if (this.routeToDetails) {
        this.$router.push({ name: "ESV", params: { esvId: esv.id } });
      } else {
        this.$emit("row:click", esv);
      }
    },
    async getDocumentTypes() {
      if (
        !this.headerSelects["documentType"] ||
        this.headerSelects["documentType"].length === 0
      ) {
        try {
          const resp = await this.$api.getServiceRequestTypes();

          // Filter alleen ESV en RNS
          const filtered = resp.data.filter(item =>
            item.text === "ESV" || item.text === "RNS"
          );

          // Direct in headerSelects zetten zonder map
          this.$set(this.headerSelects, "documentType", filtered);
          console.log("Filtered documentTypes:", filtered);
        } catch (err) {
          console.error("❌ Error fetching documentType:", err);
        }
      }
    },
    async getOrganisations() {
      if (
        !this.headerSelects["organisation"] ||
        this.headerSelects["organisation"].length === 0
      ) {
        try {
          const resp = await this.$api.getOrganisations();
          this.$set(this.headerSelects, "organisation", resp.data);
        } catch (err) {
          console.error("❌ Error fetching organisations:", err);
        }
      }
    },
    async getCountries() {
      if (
        !this.headerSelects["country"] ||
        this.headerSelects["country"].length === 0
      ) {
        try {
          const resp = await this.$api.getCountries();
          this.$set(this.headerSelects, "country", resp.data);
        } catch (err) {
          console.error("Error fetching countries:", err);
        }
      }
    },
    async getSBMProfiles() {
      if (
        !this.headerSelects["sbmProfile"] ||
        this.headerSelects["sbmProfile"].length === 0
      ) {
        try {
          const resp = await this.$api.getSBMProfiles();
          this.$set(this.headerSelects, "sbmProfile", resp.data);
        } catch (err) {
          console.error("❌ Error fetching sbmProfiles:", err);
        }
      } else {
      }
    },
    getStatusses() {
      if (
        this.headerSelects["status"] &&
        this.headerSelects["status"].length > 0
      ) {
        this.$api
          .getStatusses()
          .then((resp) => (this.headerSelects["status"] = resp.data));
      }
    },
    async getServiceRequestTypes() {
      try {
        const resp = await this.$api.getServiceRequestTypes();
        this.$set(this.headerSelects, "serviceRequestType", resp.data);
      } catch (err) {
        console.error("❌ Error fetching serviceRequestTypes:", err);
      }
    },
    async getServiceGrades() {
      try {
        const resp = await this.$api.getServiceGrades();

        const filtered = resp.data.filter((item) => item.value !== 32);

        this.$set(this.headerSelects, "serviceGrade", filtered);
      } catch (err) {
        console.error("❌ Error fetching serviceGrades:", err);
      }
    },

    async getProductGroups() {
      try {
        const resp = await this.$api.getProductGroups();
        this.$set(this.headerSelects, "productGroup", resp.data);
      } catch (err) {
        console.error("❌ Error fetching productGroups:", err);
      }
    },

    async getProductSubGroups() {
      try {
        const groups = this.search.productGroup;

        if (!groups || groups.length === 0) {
          this.headerSelects.productSubGroup = [];
          return;
        }

        const resp = await this.$api.getProductSubGroupsMulti(groups);

        this.headerSelects.productSubGroup = resp.data;
      } catch (err) {
        console.error("❌ Error fetching productSubGroups:", err);
      }
    },

    async getSBMProfiles() {
      if (
        !this.headerSelects["sbmProfile"] ||
        this.headerSelects["sbmProfile"].length === 0
      ) {
        try {
          const resp = await this.$api.getSBMProfiles();
          this.$set(this.headerSelects, "sbmProfile", resp.data);
        } catch (err) {
          console.error("❌ Error fetching sbmProfiles:", err);
        }
      }
    },
    async loadHeaderSelects() {
      await Promise.all([
        this.getCountries(),
        this.getOrganisations(),
        this.getDocumentTypes(),
        this.getServiceGrades(),
        this.getProductGroups(),
        this.getSBMProfiles(),
      ]);
    },
    searchUpdated: _.throttle(function () {
      let sendSearch = {
        options: this.options,
        search: this.search,
        ignoreServiceLimitTime: !this.showServiceLimitTime,
      };
      this.$emit("getEsvs", sendSearch);
    }, 500),

    itemRowBackground(item) {
      if (!this.showServiceLimitTime) {
        //this.search["ignoreServiceLimitTime"]){
        return item.isActive
          ? "activeEsv cursor-pointer"
          : "inactiveEsv cursor-pointer";
      } else if (this.showHasApproved) {
        return item.hasApproved
          ? "activeEsv cursor-pointer"
          : "inactiveEsv cursor-pointer";
      }
      return "cursor-pointer";
    },
    onProductGroupSelect() {
      this.search["productSubGroup"] = null;
      this.getProductSubGroups();
    },
  },
  created() {
    this.$watch(() => this.search["productGroup"], this.onProductGroupSelect);
  },
  async mounted() {
    try {
      await this.loadHeaderSelects();
      if (this.isEdit && this.ruleId) {
        await this.loadRule(this.ruleId);
      }
    } catch (err) {
      console.error("❌ Fout bij laden van header selects:", err);
    }
  },

  watch: {
    includesESV(newVal, oldVal) {
      // ESV was er, maar is nu weg
      if (oldVal === true && newVal === false) {
        this.search.serviceGrade = null;
      }
    },
    esvs(newVal) {
      if (newVal.length > 0 && this.loadedRule) {
        this.internalSelected = newVal.slice(0, 2);
        this.tableKey = Date.now();
      }
    },
    options: {
      handler() {
        this.searchUpdated();
      },
      deep: true,
    },
    showServiceLimitTime: {
      handler() {
        if (this.loading) {
          this.pendingSearchUpdate = true;
        } else {
          this.searchUpdated();
        }
      },
    },
    loading: {
      handler(newValue) {
        if (!newValue && this.pendingSearchUpdate) {
          this.pendingSearchUpdate = false;
          this.searchUpdated();
        }
      },
    },
  },
};
</script>

<style >
.activeEsv {
  background-color: rgb(143, 255, 152);
}
.inactiveEsv {
  background-color: rgb(255, 124, 124);
}

.required .v-input__control .v-input__slot {
  border-left: 3px solid dodgerblue;
}
.dotted ::v-deep(fieldset) {
  border-left: 3px dotted dodgerblue !important;
}
</style>